import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles, ArrowRight, Target, Eye, Heart, Users, Zap, ShieldCheck,
  TrendingUp, Brain, Award, Lightbulb, Linkedin
} from "lucide-react";
import PublicHeader from "@/components/PublicHeader";

const VALUES = [
  { icon: Heart, t: "Cliente em primeiro lugar", d: "Cada decisão começa com 'isso ajuda o lojista a vender mais hoje?'" },
  { icon: Brain, t: "IA prática, não acadêmica", d: "Modelos que dão respostas claras, não relatórios de 80 páginas." },
  { icon: Zap, t: "Simplicidade radical", d: "Se o usuário não entende em 30 segundos, refazemos a feature." },
  { icon: ShieldCheck, t: "Transparência total", d: "Mostramos como calculamos cada número. Sem caixas-pretas." },
  { icon: TrendingUp, t: "Resultado mensurável", d: "Métricas claras de impacto: ruptura, capital liberado, margem." },
  { icon: Users, t: "Construído com lojistas", d: "Cada feature foi validada por varejistas reais antes de sair." },
];

const TEAM = [
  { name: "Ricardo Barbosa", role: "Founder", bio: "Empreendedor e apaixonado por varejo. Fundou o SmartStockAI para levar previsão de demanda e inteligência de estoque a todo lojista brasileiro.", initials: "RB" },
];

const MILESTONES = [
  { y: "2024", t: "Início da pesquisa com 50 lojistas brasileiros sobre dores de gestão de estoque." },
  { y: "2025", t: "Lançamento beta com 12 lojas-piloto. Primeira redução média de 40% em ruptura." },
  { y: "2026", t: "Lançamento público no Brasil com Copiloto IA e 11 setores benchmarked." },
];

export default function About() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      <PublicHeader active="sobre" />

      {/* MANIFESTO */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 pt-24 pb-20">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-4">Nosso manifesto</p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold leading-[1.1] tracking-tight">
            Cada venda perdida é uma <span className="text-primary">história não contada.</span>
          </h1>
          <div className="mt-10 space-y-6 text-lg text-foreground/85 leading-relaxed">
            <p>
              No Brasil, lojistas perdem em média <strong className="text-foreground">R$ 60 bilhões por ano</strong> em vendas
              que simplesmente não acontecem — porque o produto certo não estava na prateleira na hora certa.
              Ao mesmo tempo, bilhões ficam parados em estoque que ninguém quer comprar.
            </p>
            <p>
              Acreditamos que isso não é um problema de logística. É um problema de <strong className="text-foreground">visibilidade</strong>.
              O lojista médio toma decisões de compra olhando para o passado — uma planilha, um feeling, uma intuição construída ao longo de anos.
            </p>
            <p className="text-2xl font-display text-primary">
              SmartStockAI existe para mudar isso.
            </p>
            <p>
              Construímos uma plataforma que mostra o futuro do estoque em segundos. Que prevê, alerta, recomenda e explica.
              Que faz o que um analista sênior faria — só que para 100% dos seus produtos, todos os dias, em tempo real.
            </p>
            <p>
              Nosso compromisso é simples: <strong className="text-foreground">se você não vê valor real em 30 dias, devolvemos seu investimento.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* MISSION VISION VALUES */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <article className="p-8 rounded-xl border border-primary/30 bg-card glow-coral">
            <div className="w-11 h-11 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center mb-5">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Missão</p>
            <h3 className="font-display text-2xl font-bold mb-3">Eliminar ruptura no varejo brasileiro.</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Dar a cada lojista do Brasil — independente do porte — acesso à mesma inteligência preditiva
              que grandes redes usam, por uma fração do custo.
            </p>
          </article>

          <article className="p-8 rounded-xl border border-border bg-card">
            <div className="w-11 h-11 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mb-5">
              <Eye className="w-5 h-5 text-blue-400" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-2">Visão</p>
            <h3 className="font-display text-2xl font-bold mb-3">Ser o cérebro de estoque do varejo da América Latina até 2030.</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Queremos que a expressão "tomar decisão de compra no feeling" desapareça do vocabulário do lojista.
            </p>
          </article>

          <article className="p-8 rounded-xl border border-border bg-card">
            <div className="w-11 h-11 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-5">
              <Heart className="w-5 h-5 text-emerald-400" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Propósito</p>
            <h3 className="font-display text-2xl font-bold mb-3">Devolver tempo e dinheiro a quem move o varejo.</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Cada hora que um lojista não passa em planilhas é uma hora investida no que importa: vender, atender e crescer.
            </p>
          </article>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Valores que nos guiam</p>
          <h2 className="font-display text-4xl font-bold mb-10 tracking-tight">Princípios não negociáveis.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v) => (
              <article key={v.t} className="p-6 rounded-xl border border-border bg-card">
                <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                  <v.icon className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-display font-semibold mb-2">{v.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-20 border-t border-border/50">
        <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Nossa trajetória</p>
        <h2 className="font-display text-4xl font-bold mb-10 tracking-tight">Como chegamos até aqui.</h2>
        <div className="space-y-5">
          {MILESTONES.map((m) => (
            <div key={m.y} className="flex gap-6 items-start">
              <div className="font-display text-2xl font-bold text-primary shrink-0 w-20">{m.y}</div>
              <div className="flex-1 p-5 rounded-xl border border-border bg-card">
                <p className="text-sm leading-relaxed">{m.t}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TEAM */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Fundador</p>
          <h2 className="font-display text-4xl font-bold tracking-tight">A pessoa por trás do SmartStockAI.</h2>
        </div>
        <div className="grid grid-cols-1 gap-6 max-w-md mx-auto">
          {TEAM.map((t) => (
            <article key={t.name} className="p-7 rounded-xl border border-border bg-card text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary/40 mx-auto flex items-center justify-center font-display text-2xl font-bold text-white mb-5">
                {t.initials}
              </div>
              <div className="font-display text-lg font-semibold">{t.name}</div>
              <div className="text-xs text-primary mt-1 mb-4">{t.role}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{t.bio}</p>
              <button className="mt-5 inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition">
                <Linkedin className="w-3.5 h-3.5" /> LinkedIn
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Por que SmartStockAI?</p>
            <h2 className="font-display text-4xl font-bold mb-6 tracking-tight">Não somos mais um ERP.</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              ERPs te dizem o que <em>aconteceu</em>. SmartStockAI te diz o que <strong className="text-foreground">vai acontecer</strong>.
              Foi desenhada do zero como uma camada de inteligência que conecta com qualquer sistema que você já usa
              — Bling, Tiny, Shopify, VTEX, ou seu próprio PDV.
            </p>
            <Link to="/signup">
              <Button size="lg" className="bg-primary hover:bg-primary/90 h-12 px-7">
                Experimentar grátis <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Brain, t: "IA preditiva real", d: "Não é dashboard bonito. É previsão." },
              { icon: Zap, t: "Setup em 30s", d: "Sem implantação cara." },
              { icon: Award, t: "Multi-setor", d: "11 segmentos benchmarked." },
              { icon: Lightbulb, t: "Copiloto em PT-BR", d: "Conversa de verdade." },
            ].map((d) => (
              <div key={d.t} className="p-5 rounded-xl border border-border bg-card">
                <d.icon className="w-5 h-5 text-primary mb-3" />
                <div className="font-display font-semibold text-sm">{d.t}</div>
                <div className="text-xs text-muted-foreground mt-1">{d.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-24 border-t border-border/50">
        <div className="text-center">
          <h2 className="font-display text-4xl font-bold tracking-tight">Junte-se à nova geração de lojistas.</h2>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">Comece grátis em 30 segundos. Sem cartão.</p>
          <Link to="/signup">
            <Button size="lg" className="mt-8 bg-primary hover:bg-primary/90 h-12 px-8">
              Criar minha conta grátis <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="relative z-10 border-t border-border/50 py-8 text-center text-sm text-muted-foreground">
        © 2026 SmartStockAI · Inteligência preditiva para o varejo
      </footer>
    </div>
  );
}
